import { amendStage, beginStage, clearStage, currentStage, isJourneyStage, journeyToken, retireStages, DEFERRED_CANCEL_COOKIE, DEFERRED_RETIRE_COOKIE, JourneyStages } from '@cognitoforms/api/services/journey-context';
import { buildCancelSignal, buildOrphanStepSignal, buildStartSignal, buildStepSignal, dimensionName, isReservedDimension, journeyAttributes } from '@cognitoforms/api/services/journey-envelope';
import { experimentFlags } from './experiment-flags';
import { hasFieldValue, resolveChangePlanStart, resolveCheckoutDimensions, resolveCheckoutError, resolveCheckoutStart, resolveCreateOrganizationDimensions, resolveCreateOrganizationStart, resolveFieldChoice, resolveFieldKind, resolveSignupUserStart, resolveTemplateSignupId, resolveTemplateSignupStart, resolveUpgradeStart } from './registry';
import { trackClickEvent } from '../telemetry';
import { CognitoConfiguration } from '@cognitoforms/utils/clientside-configuration';
import session from '@cognitoforms/api/services/session';
import type { ClickAnalyticsLike } from '../telemetry';
import type { JourneySignal, JourneySignalContext, StageDimensions } from '@cognitoforms/api/services/journey-envelope';
import type { ActiveStage, JourneyStage, StageStart } from '@cognitoforms/api/services/journey-context';
import type { CheckoutContext, CheckoutErrorFact } from './registry';

export const JOURNEY_SELECTOR = '[data-cf-start],[data-cf-step]';

const START_ATTRIBUTE = 'data-cf-start';
const STEP_ATTRIBUTE = 'data-cf-step';
const STAGE_ATTRIBUTE = 'data-cf-stage';
const VARIANT_ATTRIBUTE = 'data-cf-variant';
const ENTRY_POINT_ATTRIBUTE = 'data-cf-entry-point';
const DETAIL_ATTRIBUTE = 'data-cf-detail';
const COMPLETED_ATTRIBUTE = 'data-cf-completed';
const FIELD_ATTRIBUTE = 'data-cf-field';

const POINTER_FOCUS_WINDOW_MS = 200;

/**
 * The attribute that carries a form's id on any element the journey listens to. It is also the `FormId`
 * identity dimension on every event emitted from such an element.
 */
export const FORM_ID_ATTRIBUTE = 'data-cf-form-id';

/**
 * The attributes that say what an element is rather than what it carries: which role it plays and which
 * stage it names. Every other `data-cf-` attribute on an element the journey emits for becomes a dimension
 * named after it, including the ones a Start is also described by.
 */
const STRUCTURAL_ATTRIBUTES: readonly string[] = [
	START_ATTRIBUTE,
	STEP_ATTRIBUTE,
	STAGE_ATTRIBUTE,
	'data-cf-id',
	'data-cf-element-id'
];

const MAX_CANCEL_REASON_LENGTH = 64;

let lastEmittedTag: string | null = null;
let lastPointerDownAt = 0;
let fieldAttempt: string | null = null;
const fieldHadValue = new Map<string, boolean>();
let installed = false;
let pluginProvider: (() => ClickAnalyticsLike | null) | null = null;

function warn(message: string, error: unknown): void {
	if (CognitoConfiguration.IsProduction === false)
		console.warn(message, error);
}

/**
 * Whether the journey listener already emitted the event for this tag, which is how the initializer drops
 * the Click Analytics plugin's duplicate copy of the same activation.
 */
export function wasEmittedByJourney(tag: string): boolean {
	return lastEmittedTag === tag;
}

export function resetJourneyEmission(): void {
	lastEmittedTag = null;
}

function signalContext(stage: JourneyStage, element?: Element): JourneySignalContext {
	const context: JourneySignalContext = {};

	const visitorId = CognitoConfiguration.ReferrerId?.trim();
	if (visitorId)
		context.visitorId = visitorId;

	const info = session.value;
	if (info) {
		context.organizationId = info.organizationId ?? undefined;
		context.userId = info.userId ?? undefined;
	}

	const formId = element?.getAttribute(FORM_ID_ATTRIBUTE)?.trim();
	if (formId)
		context.formId = formId;

	context.experimentFlags = experimentFlags(stage);

	return context;
}

function collectDataAttributes(element: Element): Record<string, string> {
	const attrs: Record<string, string> = {};
	for (const attr of Array.from(element.attributes)) {
		if (attr.name.startsWith('data-cf-'))
			attrs[attr.name] = attr.value;
	}
	return attrs;
}

function emitSignal(plugin: ClickAnalyticsLike | null, attrs: Record<string, string>, signal: JourneySignal, tag: string): void {
	const elementId = attrs['data-cf-id'];
	if (elementId && elementId !== signal.name)
		attrs['data-cf-element-id'] = elementId;
	attrs['data-cf-id'] = signal.name;

	lastEmittedTag = tag;
	trackClickEvent(plugin, attrs, { ...signal.measurements, ...signal.dimensions });
}

/**
 * The dimensions an element names for itself. Every `data-cf-` attribute the journey does not read
 * structurally is emitted under the name it asks for, so a Start or a Step carries whatever the surface
 * knows about it without either side declaring the dimension anywhere.
 */
function declaredDimensions(attrs: Record<string, string>): Record<string, string> {
	const named: Record<string, string> = {};

	for (const [attribute, value] of Object.entries(attrs)) {
		if (STRUCTURAL_ATTRIBUTES.includes(attribute))
			continue;

		const name = dimensionName(attribute);
		if (name)
			named[name] = value;
	}

	return sanitizeDimensions(named);
}

/**
 * The dimensions as they can be emitted: named values put through {@link journeyToken}, with anything empty
 * or named after a key the plugin writes for itself dropped.
 */
function sanitizeDimensions(named: Record<string, string>): Record<string, string> {
	const dimensions: Record<string, string> = {};

	for (const [name, value] of Object.entries(named)) {
		if (!value)
			continue;

		if (isReservedDimension(name)) {
			warn('journey dimension dropped, its name is reserved by the Click Analytics plugin:', name);
			continue;
		}

		const token = journeyToken(value);
		if (token)
			dimensions[name] = token;
	}

	return dimensions;
}

function sanitizeMeasurements(measurements: Record<string, number> = {}): Record<string, number> {
	const sanitized: Record<string, number> = {};

	for (const [name, value] of Object.entries(measurements)) {
		if (!name || isReservedDimension(name) || typeof value !== 'number' || !Number.isFinite(value) || value < 0)
			continue;

		sanitized[name] = Math.round(value * 100) / 100;
	}

	return sanitized;
}

const stageDimensionProviders: Partial<Record<JourneyStage, () => Record<string, string>>> = {};

export function provideStageDimensions(stage: JourneyStage, provider: () => Record<string, string>): () => void {
	stageDimensionProviders[stage] = provider;

	return () => {
		if (stageDimensionProviders[stage] === provider)
			delete stageDimensionProviders[stage];
	};
}

function providedDimensions(stage: string | undefined): Record<string, string> {
	const provider = stage && isJourneyStage(stage) ? stageDimensionProviders[stage] : undefined;
	if (!provider)
		return {};

	try {
		return sanitizeDimensions(provider());
	}
	catch (e) {
		warn('journey dimensions failed:', e);
		return {};
	}
}

function emit(plugin: ClickAnalyticsLike | null, element: Element, signal: JourneySignal, tag: string): void {
	const attrs = collectDataAttributes(element);
	emitSignal(plugin, attrs, { ...signal, dimensions: { ...providedDimensions(signal.dimensions.Stage), ...declaredDimensions(attrs), ...signal.dimensions } }, tag);
}

/**
 * Opens a stage from code, for a Start that is an arrival rather than a click. The stage is opened before
 * the event is emitted and independently of it, so a blocked telemetry plugin still leaves the correlation
 * id riding the request header. `dimensions` are named as they are reported, the same as {@link recordStep}
 * takes them, and `elementId` is the click id the Start would have carried had it been made in markup.
 */
export function startStage(start: StageStart, dimensions: Record<string, string> = {}, elementId?: string): ActiveStage | null {
	try {
		const active = beginStage(start);
		const signal = buildStartSignal(active, signalContext(active.stage));
		const attrs = journeyAttributes(dimensions);
		if (elementId)
			attrs['data-cf-id'] = elementId;
		emitSignal(
			pluginProvider ? pluginProvider() : null,
			attrs,
			{ ...signal, dimensions: { ...sanitizeDimensions(dimensions), ...signal.dimensions } },
			`start:${start.stage}`);
		return active;
	}
	catch (e) {
		warn('journey start failed:', e);
		return null;
	}
}

/**
 * What a step says about itself, beyond its name. `variant`, `entryPoint` and `detail` are also written back
 * onto the open stage where it has not got them already, so a value the Start could not know rides
 * `X-CF-Stage` from here on and reaches the stage's Complete.
 */
export type StepOptions = {
	/** The stage the step belongs to. Omit for the most recently opened one. */
	stage?: JourneyStage;
	formId?: string;
	variant?: string;
	entryPoint?: string;
	detail?: string;
	/** Anything else the step wants carried, under the dimension names given. */
	dimensions?: Record<string, string>;
	measurements?: Record<string, number>;
};

/**
 * The three standard slots a step declares, bounded the same way any other emitted value is — so a stage
 * amended by a step holds exactly what the step's own signal carries.
 */
function stageDimensions(options: StepOptions): StageDimensions {
	return {
		variant: journeyToken(options.variant ?? '') || undefined,
		entryPoint: journeyToken(options.entryPoint ?? '') || undefined,
		detail: journeyToken(options.detail ?? '') || undefined
	};
}

/**
 * Records a step from code, for something that happens inside a stage without a click to hang
 * `data-cf-step` on. A step reached with no stage open is emitted without a correlation id where it names
 * its own stage, and dropped where it does not.
 */
export function recordStep(step: string, options: StepOptions = {}): void {
	try {
		if (!step)
			return;

		const declared = stageDimensions(options);

		let active = currentStage(options.stage);
		if (active && (declared.variant || declared.entryPoint || declared.detail))
			active = amendStage(active.stage, declared) ?? active;

		const stage = active?.stage ?? options.stage;
		if (!stage)
			return;

		const context = signalContext(stage);
		if (options.formId)
			context.formId = options.formId;

		const signal = active
			? buildStepSignal(step, active, context)
			: options.stage ? buildOrphanStepSignal(step, options.stage, context, declared) : null;

		if (!signal)
			return;

		emitSignal(
			pluginProvider ? pluginProvider() : null,
			{},
			{
				...signal,
				dimensions: { ...providedDimensions(stage), ...sanitizeDimensions(options.dimensions ?? {}), ...signal.dimensions },
				measurements: sanitizeMeasurements(options.measurements)
			},
			`step:${step}`);
	}
	catch (e) {
		warn('journey step failed:', e);
	}
}

/**
 * Opens a stage for a surface that can be reached either cold or from inside an attempt it already belongs to,
 * such as a template card — which both begins a creation and narrows one someone began by browsing. An attempt
 * already open under the same variant is stepped rather than reopened, so it keeps the one Start it began with
 * and stays attributed to where it began; anything else, including an attempt at a different variant, opens a
 * new stage as usual. `dimensions` ride whichever of the two is emitted.
 */
export function startOrStep(start: StageStart, step: string, dimensions?: Record<string, string>): void {
	const open = currentStage(start.stage);
	if (open && open.variant === start.variant)
		recordStep(step, { stage: start.stage, detail: start.detail, dimensions });
	else
		startStage(start, dimensions);
}

/**
 * Opens the paid-conversion stage for a surface that is showing someone the plans, and does nothing where
 * one is already open — {@link startStage} would otherwise mint a second correlation id and orphan the
 * intent already recorded against the first. `dimensions` are forwarded to the Start as {@link startStage} takes them.
 */
export function startUpgradeStage(source?: string, details?: string, isSignUp = false, dimensions?: Record<string, string>): ActiveStage | null {
	if (currentStage(JourneyStages.ConvertToPaid))
		return null;

	return startStage(resolveUpgradeStart(source, details, isSignUp), dimensions);
}

export function checkoutArrival(context: CheckoutContext): ActiveStage | null {
	if (context.isWalletRefill)
		return null;

	return currentStage(context.isFirstPaidPlan ? JourneyStages.ConvertToPaid : JourneyStages.ChangePlan);
}

export function startCheckoutStage(context: CheckoutContext): ActiveStage | null {
	const arrival = checkoutArrival(context);

	return startStage(resolveCheckoutStart(context, arrival), resolveCheckoutDimensions(context, arrival));
}

export function recordCheckoutStep(name: string, options: Omit<StepOptions, 'stage'> = {}): void {
	if (currentStage(JourneyStages.Checkout))
		recordStep(name, { ...options, stage: JourneyStages.Checkout });
}

export function recordCheckoutError(fact: CheckoutErrorFact): void {
	const signal = resolveCheckoutError(fact);
	if (signal)
		recordCheckoutStep('CheckoutErrorShown', signal);
}

export function recordCheckoutLoadFailed(reason: 'UnknownPlan' | 'LoadError', isFirstPaidPlan: boolean | null, statusCode?: number): void {
	const stage = isFirstPaidPlan === null
		? [JourneyStages.ConvertToPaid, JourneyStages.ChangePlan].find(open => currentStage(open))
		: isFirstPaidPlan ? JourneyStages.ConvertToPaid : JourneyStages.ChangePlan;
	if (!stage)
		return;

	const measurements = typeof statusCode === 'number' && statusCode > 0 ? { StatusCode: statusCode } : undefined;
	recordStep('CheckoutLoadFailed', { stage, dimensions: { Reason: reason }, measurements });
}

/**
 * Opens the plan-change stage for a surface where an organization already paying can change plan, and does
 * nothing where one is already open, so plan cards rendered twice on one page, or a checkout reached from
 * them, stay on the attempt that began it. `dimensions` are forwarded to the Start as {@link startStage} takes them,
 * and `source` and `details` name what sent the organization there the way {@link startUpgradeStage} takes them.
 */
export function startChangePlanStage(dimensions?: Record<string, string>, source?: string, details?: string): ActiveStage | null {
	if (currentStage(JourneyStages.ChangePlan))
		return null;

	return startStage(resolveChangePlanStart(source, details), dimensions);
}

/**
 * Records a step against a stage captured before the request the step describes, whose response can retire
 * the stage before the step is known. Emits nothing the live stage would not, beyond the dimensions given.
 */
export function recordStepFor(active: ActiveStage, step: string, dimensions: Record<string, string> = {}, measurements?: Record<string, number>): void {
	try {
		if (!step)
			return;

		const signal = buildStepSignal(step, active, signalContext(active.stage));
		emitSignal(
			pluginProvider ? pluginProvider() : null,
			{},
			{
				...signal,
				dimensions: { ...providedDimensions(active.stage), ...sanitizeDimensions(dimensions), ...signal.dimensions },
				measurements: sanitizeMeasurements(measurements)
			},
			`step:${step}`);
	}
	catch (e) {
		warn('journey step failed:', e);
	}
}

/**
 * Opens the organization-creation stage for an arrival at the create-organization page, and does nothing
 * where one is already open. `returnUrl` is the offer the visitor arrived under
 */
export function startCreateOrganizationStage(entry?: string, returnUrl?: unknown): ActiveStage | null {
	if (currentStage(JourneyStages.CreateOrganization))
		return null;

	return startStage(resolveCreateOrganizationStart(entry), resolveCreateOrganizationDimensions(returnUrl));
}

let claimedBlankFormCreation: ActiveStage | null = null;

export function openBlankFormCreation(dimensions?: Record<string, string>): void {
	const open = currentStage(JourneyStages.CreateForm);
	const openedOnThisPage = open?.variant === 'Blank' && open.startedAt >= performance.timeOrigin && open.stageCorrelationId !== claimedBlankFormCreation?.stageCorrelationId;
	const active = openedOnThisPage ? open : startStage({ stage: JourneyStages.CreateForm, variant: 'Blank', entryPoint: 'DirectNavigation' });
	if (!active)
		return;

	claimedBlankFormCreation = active;
	recordStep('BuilderOpened', { stage: JourneyStages.CreateForm, dimensions });
}

export function recordBlankFormCreationStep(step: string, dimensions?: Record<string, string>): void {
	if (currentStage(JourneyStages.CreateForm)?.variant !== 'Blank')
		return;

	recordStep(step, { stage: JourneyStages.CreateForm, dimensions });
}

export function recordClaimedBlankFormCreationStep(step: string, dimensions?: Record<string, string>): void {
	if (claimedBlankFormCreation)
		recordStepFor(claimedBlankFormCreation, step, dimensions);
	else
		recordBlankFormCreationStep(step, dimensions);
}

/**
 * Opens the signup stage for an arrival at the signup or login page, and does nothing where the arrival
 * continues the attempt already open: a return from an external provider, which names that attempt's
 * `stageCorrelationId`, or a reload of the arrival that opened it. Any other arrival opens its own.
 */
export function startSignupStage(source?: unknown, returnUrl?: unknown, isInvitation = false, stageCorrelationId?: unknown): ActiveStage | null {
	const start = resolveSignupUserStart(source, returnUrl, isInvitation);
	const open = currentStage(JourneyStages.SignupUser);
	const continuesOpen = open && (open.stageCorrelationId === stageCorrelationId || (open.variant === start.variant && open.entryPoint === start.entryPoint));
	if (continuesOpen)
		return null;

	return startStage(start);
}

export function declaredFieldDimensions(element: Element | null | undefined): Record<string, string> {
	if (!element)
		return {};

	const attrs = collectDataAttributes(element);
	delete attrs[FIELD_ATTRIBUTE];
	return declaredDimensions(attrs);
}

export function recordFieldFocused(field: string, via: string, dimensions: Record<string, string> = {}): void {
	if (!resolveFieldKind(field) || !currentStage(JourneyStages.Checkout))
		return;

	recordStep('FieldFocused', { stage: JourneyStages.Checkout, dimensions: { ...dimensions, Field: field, Via: via } });
}

export function recordFieldValue(field: string, value: unknown, options: { previous?: unknown } = {}): void {
	const kind = resolveFieldKind(field);
	const active = currentStage(JourneyStages.Checkout);
	if (!kind || !active)
		return;

	if (fieldAttempt !== active.stageCorrelationId) {
		fieldAttempt = active.stageCorrelationId;
		fieldHadValue.clear();
	}

	const had = Object.prototype.hasOwnProperty.call(options, 'previous') ? hasFieldValue(field, options.previous) : fieldHadValue.get(field) === true;
	const has = hasFieldValue(field, value);
	fieldHadValue.set(field, has);

	let step: string;
	if (!had && has)
		step = 'FieldSet';
	else if (had && has)
		step = 'FieldChanged';
	else if (had && !has)
		step = 'FieldCleared';
	else
		return;

	if (kind === 'Card' && step === 'FieldChanged')
		return;

	const dimensions: Record<string, string> = { Field: field };
	const choice = kind === 'Choice' && (has || typeof value === 'boolean') ? resolveFieldChoice(field, value) : undefined;
	if (choice)
		dimensions.Choice = choice;

	const measurements: Record<string, number> = {};
	if (kind === 'Amount')
		measurements.Value = typeof value === 'number' ? value : 0;

	recordStep(step, { stage: JourneyStages.Checkout, dimensions, measurements });
}

export function startTemplateSignupStage(source?: unknown, details?: unknown): ActiveStage | null {
	const start = resolveTemplateSignupStart(source);
	if (!start)
		return null;

	const open = currentStage(JourneyStages.CreateForm);
	if (open && open.variant === start.variant && open.entryPoint === start.entryPoint)
		return null;

	const templateId = resolveTemplateSignupId(details);
	return startStage(start, templateId ? { TemplateId: templateId } : {});
}

function takeCookie(name: string): string | null {
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
	if (!match)
		return null;

	document.cookie = `${name}=;path=/;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
	return decodeURIComponent(match[1]);
}

/**
 * Closes the stages the server retired on a navigation whose response no script could read, which it left on
 * {@link DEFERRED_RETIRE_COOKIE}. Redirect-mode SSO completes SignupUser on exactly such a request, and an
 * invited signup lands straight in its organization rather than passing anywhere that would notice. Emits
 * nothing — the events were already recorded server side; this only stops a spent stage riding `X-CF-Stage`.
 */
export function applyDeferredRetirement(): void {
	try {
		retireStages(takeCookie(DEFERRED_RETIRE_COOKIE));
	}
	catch (e) {
		warn('journey retirement failed:', e);
	}
}

/**
 * Emits the cancellation the server left on {@link DEFERRED_CANCEL_COOKIE} because the navigation it found
 * the contradiction on had no response a script would read, and closes the stage it retracts. The cookie is
 * consumed whether or not it turns out to be usable.
 */
export function applyDeferredCancellation(): void {
	try {
		const value = takeCookie(DEFERRED_CANCEL_COOKIE);
		if (!value)
			return;

		const separator = value.indexOf(':');
		if (separator < 1)
			return;

		const stage = value.slice(0, separator);
		const reason = value.slice(separator + 1);
		if (!isJourneyStage(stage) || !isCancelReason(reason))
			return;

		const active = currentStage(stage);
		if (!active)
			return;

		clearStage(stage);
		emitSignal(pluginProvider ? pluginProvider() : null, {}, buildCancelSignal(active, reason, signalContext(stage)), `cancel:${stage}`);
	}
	catch (e) {
		warn('journey cancellation failed:', e);
	}
}

/**
 * Whether the reason on the cookie is one we could have written — a bare token of the same alphabet every
 * other journey dimension uses.
 */
function isCancelReason(value: string): boolean {
	return value.length > 0 && journeyToken(value, MAX_CANCEL_REASON_LENGTH) === value;
}

/**
 * The stage a step names, from the element or the nearest ancestor that names one, so a shared component can
 * name it once for everything it wraps. A value that is not a stage is ignored rather than searched past,
 * falling through to the most recently started stage.
 */
function requestedStage(element: Element): JourneyStage | undefined {
	const stage = element.closest(`[${STAGE_ATTRIBUTE}]`)?.getAttribute(STAGE_ATTRIBUTE);
	return stage && isJourneyStage(stage) ? stage : undefined;
}

/**
 * What an element says about its Start, taken from itself or from the nearest ancestor that says it, so a
 * wrapper can describe every Start inside it once.
 */
function inherited(element: Element, attribute: string): string | undefined {
	const value = element.closest(`[${attribute}]`)?.getAttribute(attribute);
	return value ? journeyToken(value) || undefined : undefined;
}

/**
 * What the element itself says, not counting what a wrapper says about the Starts inside it. A step that
 * amends the stage it is inside has to be the thing that said so.
 */
function declared(element: Element, attribute: string): string | undefined {
	const value = element.getAttribute(attribute);
	return value ? journeyToken(value) || undefined : undefined;
}

/**
 * The Start an element declares: `data-cf-start` names the stage, and `data-cf-variant`,
 * `data-cf-entry-point` and `data-cf-detail` say which flavour of it this is, where it was opened from, and
 * whatever else the surface wanted carried. A `data-cf-start` that does not name a stage opens nothing.
 */
function declaredStart(element: Element): StageStart | null {
	const stage = element.getAttribute(START_ATTRIBUTE)?.trim();
	if (!stage || !isJourneyStage(stage))
		return null;

	const start: StageStart = { stage, variant: inherited(element, VARIANT_ATTRIBUTE) ?? '' };

	const entryPoint = inherited(element, ENTRY_POINT_ATTRIBUTE);
	if (entryPoint)
		start.entryPoint = entryPoint;

	const detail = inherited(element, DETAIL_ATTRIBUTE);
	if (detail)
		start.detail = detail;

	return start;
}

/**
 * Whether the subject this element belongs to has already completed the stage, and so cannot enter it
 * again. Read from the nearest ancestor that names its completed stages, which is where the page that knows
 * the subject puts them.
 */
function alreadyCompleted(element: Element, stage: JourneyStage): boolean {
	return element.closest(`[${COMPLETED_ATTRIBUTE}~="${stage}"]`) !== null;
}

function activate(plugin: ClickAnalyticsLike | null, element: Element): void {
	if (element.hasAttribute(START_ATTRIBUTE)) {
		const start = declaredStart(element);
		if (start && !alreadyCompleted(element, start.stage))
			emit(plugin, element, buildStartSignal(beginStage(start), signalContext(start.stage, element)), `start:${start.stage}`);
		return;
	}

	const stepName = element.getAttribute(STEP_ATTRIBUTE);
	if (!stepName)
		return;

	const requested = requestedStage(element);
	let active = currentStage(requested);
	if (active) {
		active = amendStage(active.stage, {
			variant: declared(element, VARIANT_ATTRIBUTE),
			entryPoint: declared(element, ENTRY_POINT_ATTRIBUTE),
			detail: declared(element, DETAIL_ATTRIBUTE)
		}) ?? active;

		emit(plugin, element, buildStepSignal(stepName, active, signalContext(active.stage, element)), `step:${stepName}`);
		return;
	}

	if (requested)
		emit(plugin, element, buildOrphanStepSignal(stepName, requested, signalContext(requested, element)), `step:${stepName}`);
}

function resolveElement(target: EventTarget | null): Element | null {
	const element = target as Element | null;
	if (!element || typeof element.closest !== 'function')
		return null;
	return element.closest(JOURNEY_SELECTOR);
}

const TEXT_ENTRY_SELECTOR = 'textarea, input:not([type=button]):not([type=submit]):not([type=reset]):not([type=checkbox]):not([type=radio])';

/**
 * Whether the key landed somewhere Enter and Space are text rather than activation. A Start can sit on a
 * field — the publish page puts one on the embed-code textarea — and every space typed in one would
 * otherwise reopen the stage.
 */
function isTextEntry(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	if (!element || typeof element.matches !== 'function')
		return false;

	return element.matches(TEXT_ENTRY_SELECTOR) || element.isContentEditable === true;
}

/**
 * Registers the listener that opens stages and emits their Start and Step events. It runs in the capture
 * phase on `window`, ahead of every application handler and out of reach of `stopPropagation`, so the
 * correlation id exists before the handler fires the request that carries it.
 */
export function installJourneyActivation(getPlugin: () => ClickAnalyticsLike | null): void {
	pluginProvider = getPlugin;

	if (installed)
		return;
	installed = true;

	const handle = (target: EventTarget | null) => {
		try {
			const element = resolveElement(target);
			if (element)
				activate(getPlugin(), element);
		}
		catch (e) {
			warn('journey activation failed:', e);
		}
	};

	window.addEventListener('mousedown', (event: MouseEvent) => {
		lastPointerDownAt = Date.now();
		if (event.button === 0)
			handle(event.target);
	}, true);

	window.addEventListener('keydown', (event: KeyboardEvent) => {
		if ((event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') && !isTextEntry(event.target))
			handle(event.target);
	}, true);

	window.addEventListener('focusin', (event: FocusEvent) => {
		try {
			const target = event.target as Element | null;
			if (!target || typeof target.closest !== 'function')
				return;

			const element = target.closest(`[${FIELD_ATTRIBUTE}]`);
			const field = element?.getAttribute(FIELD_ATTRIBUTE);
			if (field)
				recordFieldFocused(field, Date.now() - lastPointerDownAt < POINTER_FOCUS_WINDOW_MS ? 'Pointer' : 'Keyboard', declaredFieldDimensions(element));
		}
		catch (e) {
			warn('journey field focus failed:', e);
		}
	}, true);
}
